import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth } from '../config/firebase';
import { 
  signInWithPhoneNumber, 
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { userService, authService } from '../services/api.service';
import Swal from 'sweetalert2';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUserForm, setShowUserForm] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [pendingRedirect, setPendingRedirect] = useState(null);

  // Function to set pending redirect from URL params
  const setPendingRedirectFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectUri = urlParams.get('redirect_uri');
    if (redirectUri) {
      setPendingRedirect(redirectUri);
      console.log('🔗 Pending redirect set from URL:', redirectUri);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('logout') === '1') {
        const redirectUri = urlParams.get('redirect_uri') || '/login';
        try {
          await signOut(auth);
        } catch (e) {
          console.warn('signOut on logout flow:', e?.message);
        }
        window.location.replace(redirectUri);
        return;
      }

      if (firebaseUser) {
        try {
          // Check for redirect URL in URL params for new users
          setPendingRedirectFromURL();
          
          // Establecer cookie de sesión con el backend
          const idToken = await firebaseUser.getIdToken();
          await authService.setSession(idToken);
          console.log('✅ Session cookie established');

          // Intentar obtener datos del usuario de MongoDB
          const userData = await userService.getMyProfile();
          console.log('📊 User data retrieved:', !!userData);
          console.log('✅ Profile complete:', userData?.hasCompletedProfile);
          console.log('📋 User data details:', {
            name: userData?.name,
            email: userData?.email,
            phone: userData?.phone,
            ageRange: userData?.ageRange,
            gender: userData?.gender,
            consents: userData?.consents
          });
          
          setUser({ ...firebaseUser, userData });
          
          // Only set showUserForm to false if profile is complete
          if (userData && userData.hasCompletedProfile) {
            setShowUserForm(false);
            console.log('✅ User profile is complete, not showing form');
          } else {
            setShowUserForm(true);
            console.log('📝 User profile incomplete, showing form');
          }
          
          // Obtener información de sesión
          await getSessionInfo();
        } catch (error) {
          console.error('Error establishing session or getting user data:', error);
          
          // Si no se puede establecer la sesión o el usuario no existe en MongoDB
          if (error.error === 'Unauthenticated' || error.status === 401 || error.error === 'Profile Not Found') {
            // Mostrar formulario para completar perfil
            setUser(firebaseUser);
            setShowUserForm(true);
            console.log('📝 New user or profile not found, showing form');
          } else {
            // Otros errores - mostrar formulario igualmente
            setUser(firebaseUser);
            setShowUserForm(true);
            console.log('📝 Error getting user data, showing form');
          }
        }
      } else {
        setUser(null);
        setShowUserForm(false);
        setSessionInfo(null);
        setPendingRedirect(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (phoneNumber, recaptchaVerifier) => {
    try {
      console.log('Signing in with phone number:', phoneNumber);
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      return confirmationResult;
    } catch (error) {
      console.error('Sign in error:', error);
      
      // Handle specific Firebase errors
      if (error.code === 'auth/invalid-phone-number') {
        throw new Error('Número de teléfono inválido');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Demasiados intentos. Por favor, intenta más tarde');
      } else if (error.code === 'auth/captcha-check-failed') {
        throw new Error('Verificación reCAPTCHA fallida. Recarga la página e intenta de nuevo');
      }
      
      throw error;
    }
  };

  const submitUserData = async (userData) => {
    try {
      if (!user?.uid) {
        throw new Error('No user authenticated');
      }

      const userPayload = {
        firebaseId: user.uid,
        email: userData.email || user.email,
        phone: userData.phone || user.phoneNumber,
        ...userData
      };

      const response = await userService.createUser(userPayload);
      
      // Update local user state
      setUser({ ...user, userData: response.user });
      setShowUserForm(false);
      
      Swal.fire({
        icon: 'success',
        title: '¡Perfil Completado!',
        text: 'Tu perfil ha sido creado exitosamente.',
        timer: 2000,
        showConfirmButton: false
      });

      // If there's a pending redirect, execute it now
      if (pendingRedirect) {
        console.log('🚀 Executing pending redirect after profile completion:', pendingRedirect);
        const redirectUrl = pendingRedirect;
        setPendingRedirect(null);
        
        // Small delay to ensure state updates
        setTimeout(() => {
          if (redirectUrl.startsWith('http')) {
            window.location.href = redirectUrl;
          } else {
            window.location.href = redirectUrl;
          }
        }, 100);
      }

      return response;
    } catch (error) {
      console.error('Submit user data error:', error);
      
      if (error.error === 'User Exists') {
        setUser({ ...user, userData: error.user });
        setShowUserForm(false);
        return;
      }
      
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Logout from backend first (clear session cookie)
      try {
        await authService.logout();
        console.log('✅ Backend session cleared');
      } catch (backendError) {
        console.log('Note: Backend logout failed:', backendError.message);
      }

      // Logout from Firebase
      await signOut(auth);
      setUser(null);
      setShowUserForm(false);
      setPendingRedirect(null);
      
      Swal.fire({
        icon: 'info',
        title: 'Sesión Cerrada',
        text: 'Has cerrado sesión exitosamente.',
        timer: 2000,
        showConfirmButton: false
      });
      
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      throw error;
    }
  };

  const checkUserData = async () => {
    try {
      if (!user) return false;
      
      const userData = await userService.getMyProfile();
      return userData && userData.hasCompletedProfile;
    } catch (error) {
      console.error('Error al verificar datos del usuario:', error);
      return false;
    }
  };

  const refreshUserData = async () => {
    try {
      if (!user) return;
      
      const userData = await userService.getMyProfile();
      setUser({ ...user, userData });
      return userData;
    } catch (error) {
      console.error('Error refreshing user data:', error);
      throw error;
    }
  };

  const getSessionInfo = async () => {
    try {
      const info = await authService.getSessionInfo();
      setSessionInfo(info);
      return info;
    } catch (error) {
      console.error('Error getting session info:', error);
      setSessionInfo(null);
      return null;
    }
  };

  const value = {
    user,
    loading,
    signIn,
    showUserForm,
    submitUserData,
    logout,
    checkUserData,
    refreshUserData,
    sessionInfo,
    getSessionInfo,
    pendingRedirect,
    isAuthenticated: !!user,
    hasCompletedProfile: !!(user?.userData?.hasCompletedProfile)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 